import { memo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getPassOwnerInfoAndBenefits,
  PrivilegeBenefit,
} from "../../services/EmployeeService";
import BenefitCard from "../BenefitCard/BenefitCard";
import "./PassInfo.css";

interface PassInfoState {
  imageUrl?: string;
  compagnyName?: string;
  passId?: string;
  compagnyId?: number;
  employeeName?: string;
  employeeId?: number;
}

const messageStyle = {
  color: "#ffffff",
  textAlign: "center" as const,
  padding: "24px 16px",
  opacity: 0.85,
};

const PassInfo = () => {
  const location = useLocation();
  const state = location.state as PassInfoState | null;

  const displayImageUrl = state?.imageUrl || "/placeholder.png";
  const displayCompagnyName = state?.compagnyName || "";
  const displayEmployeeName = state?.employeeName || "";
  const [passEmployee, setPassEmployee] =
    useState<EmployeeInfoByPassIdResponse>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasParams = Boolean(state?.compagnyId && state?.passId);
  interface EmployeeInfoByPassIdResponse {
    record?: {
      id: number;
      first_name: string;
      last_name: string;
      user_type?: string;
      identity_card_id?: string;
      compagny_id?: number;
      compagny?: {
        id: number;
        name: string;
      };
      benefits?: PrivilegeBenefit[];
      [key: string]: any;
    };
  }
  useEffect(() => {
    if (!state?.compagnyId || !state?.passId) return;
    const compagnyId = state.compagnyId;
    const passId = state.passId;
    let cancelled = false;

    const fetchPassOwnerInfoAndBenefits = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const result = await getPassOwnerInfoAndBenefits(compagnyId, passId);
        if (!cancelled) setPassEmployee(result);
      } catch (error) {
        console.error(
          "[PassInfo] Error fetching pass owner info and benefits:",
          error
        );
        if (!cancelled) {
          setLoadError("Unable to load this card. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPassOwnerInfoAndBenefits();
    return () => {
      cancelled = true;
    };
  }, [state?.compagnyId, state?.passId]);

  return (
    <div className="pass-info-container">
      <div
        className="pass-info-image"
        style={{ backgroundImage: `url(${displayImageUrl})` }}
      >
        {displayCompagnyName && (
          <h1 className="pass-info-title">{displayCompagnyName}</h1>
        )}
      </div>
      <div className="pass-info-content-container">
        {displayEmployeeName && (
          <p className="pass-info-employee-greeting">
            Hi {displayEmployeeName}!
          </p>
        )}
        {!hasParams ? (
          <p style={messageStyle}>
            This pass link is incomplete. Please rescan the QR code.
          </p>
        ) : loading ? (
          <p style={messageStyle}>Loading…</p>
        ) : loadError ? (
          <p style={messageStyle}>{loadError}</p>
        ) : !passEmployee.record ? (
          <p style={messageStyle}>Card not found.</p>
        ) : (
          <>
            <p className="pass-info-card-belong-text">This card belongs to:</p>
            <div className="pass-info-card-container">
              <div>
                {passEmployee.record.first_name &&
                  passEmployee.record.last_name && (
                    <div className="pass-info-owner-name">
                      {passEmployee.record.first_name}{" "}
                      {passEmployee.record.last_name}
                    </div>
                  )}
                <div className="pass-info-owner-details">
                  {passEmployee.record.compagny?.name && (
                    <div className="pass-info-compagny">
                      <img
                        src="/compagny.svg"
                        alt="Company"
                        className="pass-info-compagny-icon"
                      />
                      {passEmployee.record.compagny.name}
                    </div>
                  )}
                  {passEmployee.record.user_type && (
                    <div className="pass-info-user-type">
                      <img
                        src="/emp.svg"
                        alt="Employee"
                        className="pass-info-user-icon"
                      />
                      {passEmployee.record.user_type}
                    </div>
                  )}
                  {passEmployee.record.identity_card_id && (
                    <div className="pass-info-identity-card">
                      <img
                        src="/idCard.svg"
                        alt="ID Card"
                        className="pass-info-card-icon"
                      />
                      {passEmployee.record.identity_card_id}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <p className="pass-info-privileges-title">Available privileges</p>
            {passEmployee.record.benefits &&
            passEmployee.record.benefits.length > 0 ? (
              <div className="pass-info-benefits-list">
                {passEmployee.record.benefits.map((benefit, idx) => (
                  <BenefitCard
                    key={benefit.id ?? `${benefit.name}-${idx}`}
                    benefit={benefit}
                    showUseButton={true}
                    pass_owner_id={passEmployee.record?.id}
                    compagnie_id={
                      passEmployee.record?.compagny_id ||
                      passEmployee.record?.compagny?.id
                    }
                    employee_scanning_id={state?.employeeId ?? undefined}
                  />
                ))}
              </div>
            ) : (
              <p style={messageStyle}>No privileges available.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default memo(PassInfo);
